/**
 * FRONT-3158 / FRONT-3369: what is waiting on the owner's app, said on its
 * Edit page: changes that aren't live yet, with Publish (which first shows
 * what the publish includes), and drafts sent for review, with Review. Zero
 * of either is left unsaid rather than printed as a number.
 */
import { toast } from 'sonner';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { CircleNotch, RocketLaunchIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { publishApp, refreshAfterPublish, usePublishApp, type Published } from '~/hooks/mutations/use-publish-app';
import type { useConfirm } from '~/components/confirm-dialog';
import type { AdminApp } from './edit-app';
import { publishUndoToast } from './publish-undo';

export const PUBLISHING = 'Publishing…';
export const PUBLISHED_MESSAGE = 'Published. Your changes are live.';

type NoticeApp = Pick<AdminApp, 'slug' | 'isOwner' | 'status' | 'unpublishedChanges' | 'draftsToReview'>;

/** "1 change isn't live yet" / "6 changes aren't live yet". */
export function changesWaiting(n: number): string {
  return n === 1 ? "1 change isn't live yet" : `${n} changes aren't live yet`;
}

/** "1 draft waiting for your review" / "2 drafts waiting for your review". */
export function draftsWaiting(n: number): string {
  return n === 1 ? '1 draft waiting for your review' : `${n} drafts waiting for your review`;
}

export interface WaitingNotice {
  /** The line, its parts joined by a middle dot. */
  line: string;
  publish: boolean;
  review: boolean;
}

/**
 * What the owner's notice says and offers; null when there is nothing to say.
 * Changes are named on a live app only: offline, the Live switch publishes them.
 */
export function waitingNotice(app: Omit<NoticeApp, 'slug'>): WaitingNotice | null {
  if (!app.isOwner || app.status === 'archived') return null;
  const changes = app.status === 'live' && typeof app.unpublishedChanges === 'number' && app.unpublishedChanges > 0 ? app.unpublishedChanges : 0;
  const drafts = typeof app.draftsToReview === 'number' && app.draftsToReview > 0 ? app.draftsToReview : 0;
  if (!changes && !drafts) return null;
  return {
    line: [changes ? changesWaiting(changes) : null, drafts ? draftsWaiting(drafts) : null].filter(Boolean).join(' · '),
    publish: changes > 0,
    review: drafts > 0,
  };
}

/** Why "Publish" cannot be pressed right now; null when it can. One decision for disabled and its explanation. */
export function publishChangesGate(input: { pending: boolean }): string | null {
  return input.pending ? PUBLISHING : null;
}

/**
 * Publishes the live app's waiting changes after the owner confirms what is
 * included, then says so with an Undo for that publish (FRONT-3368), or shows
 * the server's own sentence. `previewOn`: draft previews are on, so the
 * confirmation links each accepted draft's preview.
 */
export function usePublishChanges(slug: string, previewOn: boolean) {
  const qc = useQueryClient();
  const publish = usePublishApp(
    slug,
    {
      onSuccess: (data) => toast.success(PUBLISHED_MESSAGE, publishUndoToast(qc, slug, data)),
      onError: (e: Error) => toast.error(e.message || 'Could not publish the app'),
    },
    { goingLive: false, previewOn },
  );
  return { pending: publish.isPending, run: () => publish.mutate() };
}

const savedPublishInFlight = new Set<string>();

/** `usePublishChanges` for a toast's action, which runs outside React; `goingLive` asks "Make this app live?". */
export async function publishSavedChanges(deps: {
  qc: QueryClient;
  slug: string;
  confirm: ReturnType<typeof useConfirm>;
  goingLive: boolean;
  previewOn: boolean;
  onPublished?: () => void;
}): Promise<void> {
  const { qc, slug } = deps;
  // A toast's action has no pending state, so a second click would open a second summary.
  if (savedPublishInFlight.has(slug)) return;
  savedPublishInFlight.add(slug);
  try {
    let data: Published | null;
    try {
      data = await publishApp(slug, deps.confirm, { goingLive: deps.goingLive, previewOn: deps.previewOn });
    } catch (e) {
      toast.error((e as Error).message || 'Could not publish the app');
      return;
    }
    if (!data) return;
    toast.success(PUBLISHED_MESSAGE, publishUndoToast(qc, slug, data));
    deps.onPublished?.();
    await refreshAfterPublish(qc, slug);
  } finally {
    savedPublishInFlight.delete(slug);
  }
}

export function PublishChangesNotice({ app, previewOn, onReview }: { app: NoticeApp; previewOn: boolean; onReview: () => void }) {
  const publish = usePublishChanges(app.slug, previewOn);
  const notice = waitingNotice(app);
  if (!notice) return null;
  const blockedBy = publishChangesGate({ pending: publish.pending });
  return (
    <div
      data-testid="publish-changes-notice"
      role="status"
      className="flex flex-wrap items-center gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm"
    >
      <RocketLaunchIcon size={16} className="shrink-0 text-primary" />
      <p className="flex-1" data-testid="publish-changes-line">
        {notice.line}
      </p>
      {notice.review && (
        <Button size="sm" variant="outline" data-testid="review-drafts" tooltip="Go to the drafts sent to you for review" onClick={onReview}>
          Review
        </Button>
      )}
      {notice.publish && (
        <Button
          size="sm"
          data-testid="publish-changes"
          disabled={blockedBy !== null}
          tooltip={blockedBy ?? 'See what goes live, then make the latest changes live for everyone who can open the app'}
          onClick={publish.run}
        >
          {publish.pending ? <CircleNotch size={14} className="animate-spin" /> : null}
          {blockedBy ?? 'Publish'}
        </Button>
      )}
    </div>
  );
}
